import { cardDisplayName, cardPrintedNumber } from '../utils/cardMetadata';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHotCards, type HotCardItem } from '../api/cards';

export default function HotCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState<HotCardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    let active = true;

    const fetchHotCards = async () => {
      try {
        setIsLoading(true);
        setError(false);
        const response = await getHotCards();
        if (!active) return;
        if (response.code === 200) {
          setCards(response.data);
          setUpdatedAt(response.updatedAt ?? null);
        } else {
          setError(true);
        }
      } catch (err) {
        if (active) setError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchHotCards();
    return () => { active = false; };
  }, [refreshCount]);

  const handleCardClick = (card: HotCardItem) => {
    navigate(`/card/${card.id}`, {
      state: {
        card: {
          uid: card.id,
          ...Object.fromEntries(Object.entries(card).filter(([key]) => ['card_name','printed_number','series_code','series_name','name_zh','name_en','language','metadata_status'].includes(key))),
          name: card.name,
          number: card.number,
          image_url_at_self_platform: card.image,
        },
      },
    });
  };

  if (isLoading && cards.length === 0) {
    return (
      <div>
        <p className="eyebrow mb-2 text-cyan">Market discovery</p>
        <h2 className="mb-5 text-2xl font-semibold tracking-[-0.04em] text-primary">Trending Pokémon cards</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 sm:gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-[20px] border border-border bg-white">
              <div className="aspect-[63/88] bg-gray-200" />
              <div className="p-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Market discovery</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-primary">Trending Pokémon cards</h2>
        </div>
        <button
          type="button"
          disabled={isLoading}
          onClick={() => setRefreshCount(count => count + 1)}
          className="shrink-0 text-sm font-semibold text-cyan disabled:opacity-50"
        >
          {isLoading ? 'Refreshing…' : error ? 'Retry' : 'Refresh'}
        </button>
      </div>

      {updatedAt && (
        <p className="mb-3 text-xs text-secondary">
          Updated <time dateTime={updatedAt}>{new Date(updatedAt).toLocaleString()}</time>
        </p>
      )}
      {error && (
        <p role="alert" className="mb-4 text-sm text-secondary">
          {cards.length > 0
            ? 'Could not refresh. Showing the last saved list.'
            : 'Could not load trending cards. Please retry.'}
        </p>
      )}
      {!error && cards.length === 0 && (
        <p className="text-sm text-secondary">No trending cards available yet. You can still search for cards.</p>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 md:grid-cols-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card)}
            className="group flex flex-col overflow-hidden rounded-[20px] border border-border bg-white text-left transition-all hover:-translate-y-1 hover:border-cyan/40 hover:shadow-panel"
          >
            {/* 卡片图片 */}
            <div className="relative aspect-[63/88] overflow-hidden bg-gradient-to-b from-white to-background-secondary p-2 sm:p-3">
              {card.image ? (
                <img
                  src={card.image}
                  alt={cardDisplayName(card)}
                  className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.025]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-secondary-light">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* 卡片信息 */}
            <div className="border-t border-border/70 p-3.5">
              <h3 className="truncate text-sm font-bold text-primary">{cardDisplayName(card)}</h3>
              <p className="min-h-4 truncate text-xs text-secondary-light">{card.series_code || "\u00a0"}</p>
              <p className="min-h-4 text-xs text-secondary-light mt-1 truncate">{cardPrintedNumber(card) || "\u00a0"}</p>
              {card.latest_sale && card.latest_sale.price > 0 && <>
                <p className="mt-1 text-sm font-semibold text-red-600">{card.latest_sale.currency} {card.latest_sale.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                <p className="text-[10px] text-secondary" aria-label="Latest sale">{[card.latest_sale.platform.toUpperCase(), card.latest_sale.grade_agency, card.latest_sale.grade_score, new Date(card.latest_sale.sold_time).toLocaleDateString()].filter(Boolean).join(' · ')}</p>
              </>}
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary-light">View market evidence →</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
